import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { parseRating, isVerified, submitResourceVote, getUserVote } from '../services/googleSheetService';

const { FiMapPin, FiPhone, FiGlobe, FiMail, FiClock, FiInfo, FiThumbsUp, FiThumbsDown, ExternalLink } = FiIcons;

const ResourceCard = ({ resource, index, onVoteUpdate }) => {
  const [showVotePopup, setShowVotePopup] = useState(false);
  const [isSubmittingVote, setIsSubmittingVote] = useState(false);
  const [localRating, setLocalRating] = useState(() => {
    // Parse rating from the resource
    const ratingField = resource.rating || '';
    return parseRating(ratingField);
  });
  
  const userVote = getUserVote(resource.id);
  const verified = isVerified(localRating);

  // Helper function to get the correct field value from different possible field names
  const getFieldValue = (resource, ...fieldNames) => {
    for (const fieldName of fieldNames) {
      if (resource[fieldName] && resource[fieldName].trim()) {
        return resource[fieldName].trim();
      }
    }
    return null;
  };

  const organizationName = getFieldValue(resource, 'organizationname', 'organization_name', 'name');
  const locationName = getFieldValue(resource, 'locationname', 'location_name');
  const address = getFieldValue(resource, 'address');
  const city = getFieldValue(resource, 'city');
  const zipCode = getFieldValue(resource, 'zipcode', 'zip_code');
  const phoneNumber = getFieldValue(resource, 'phonenumber', 'phone_number', 'phone');
  const alternatePhone = getFieldValue(resource, 'alternatephone', 'alternate_phone');
  const website = getFieldValue(resource, 'website');
  const email = getFieldValue(resource, 'email');
  const services = getFieldValue(resource, 'services', 'description');
  const hours = getFieldValue(resource, 'hours');
  const notes = getFieldValue(resource, 'notes');
  const section = getFieldValue(resource, 'section', 'category');

  const handleVote = async (voteType) => {
    setIsSubmittingVote(true);
    try {
      const newRating = await submitResourceVote(resource.id, voteType, localRating);
      setLocalRating(newRating);
      setShowVotePopup(false);
      
      // Notify parent component of the vote update
      if (onVoteUpdate) {
        onVoteUpdate(resource.id, newRating);
      }
    } catch (error) {
      console.error('Error submitting vote:', error);
      // You might want to show an error message to the user here
    } finally {
      setIsSubmittingVote(false);
    }
  };

  const handleContactClick = (e, action, url) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Show vote popup first
    setShowVotePopup(true);
    
    // Store the action to perform after voting
    window.pendingAction = { action, url };
  };

  const executePendingAction = () => {
    if (window.pendingAction) {
      const { action, url } = window.pendingAction;
      
      switch (action) {
        case 'phone':
          window.location.href = `tel:${url}`;
          break;
        case 'website':
          window.open(url.startsWith('http') ? url : `https://${url}`, '_blank', 'noopener,noreferrer');
          break;
        case 'email':
          window.location.href = `mailto:${url}`;
          break;
        case 'address':
          // Open Google Maps with the address
          const fullAddress = `${address}, ${city} ${zipCode}`.replace(/,\s*undefined/g, '').trim();
          const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(fullAddress)}`;
          window.open(mapsUrl, '_blank', 'noopener,noreferrer');
          break;
      }
      
      // Clear the pending action
      window.pendingAction = null;
    }
  };

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: index * 0.05 }}
        className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden relative"
      >
        {/* Verified Badge */}
        {verified && (
          <div className="absolute top-3 right-3 z-10">
            <div className="bg-green-100 text-green-800 text-xs font-semibold px-3 py-1 rounded-full flex items-center gap-1">
              <span className="text-green-600">✅</span>
              Verified
            </div>
          </div>
        )}

        <div className="p-6">
          {/* Header */}
          <div className="mb-4">
            {section && (
              <span className="inline-block bg-indigo-100 text-indigo-800 text-xs font-semibold px-3 py-1 rounded-full mb-2">
                {section}
              </span>
            )}
            {organizationName && (
              <h4 className="text-lg font-bold text-gray-800 mb-1">{organizationName}</h4>
            )}
            {locationName && (
              <p className="text-gray-600 text-sm">{locationName}</p>
            )}
          </div>

          {/* Contact Info */}
          <div className="space-y-3 mb-4">
            {address && (
              <div className="flex items-start gap-2">
                <SafeIcon icon={FiMapPin} className="text-gray-400 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-gray-600">
                  <button
                    onClick={(e) => handleContactClick(e, 'address')}
                    className="hover:text-indigo-600 transition-colors text-left"
                  >
                    <div>{address}</div>
                    <div>
                      {city && zipCode ? `${city}, ${zipCode}` : city || zipCode}
                    </div>
                  </button>
                </div>
              </div>
            )}

            {phoneNumber && (
              <div className="flex items-center gap-2">
                <SafeIcon icon={FiPhone} className="text-gray-400 flex-shrink-0" />
                <div className="text-sm text-gray-600">
                  <a
                    href={`tel:${phoneNumber}`}
                    onClick={(e) => handleContactClick(e, 'phone', phoneNumber)}
                    className="hover:text-indigo-600 transition-colors"
                  >
                    {phoneNumber}
                  </a>
                  {alternatePhone && (
                    <div className="text-xs text-gray-500">Alt: {alternatePhone}</div>
                  )}
                </div>
              </div>
            )}

            {website && (
              <div className="flex items-center gap-2">
                <SafeIcon icon={FiGlobe} className="text-gray-400 flex-shrink-0" />
                <a
                  href={website.startsWith('http') ? website : `https://${website}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => handleContactClick(e, 'website', website)}
                  className="text-sm text-indigo-600 hover:text-indigo-800 transition-colors truncate flex items-center gap-1"
                >
                  {website}
                  <SafeIcon icon={ExternalLink} className="text-xs" />
                </a>
              </div>
            )}

            {email && (
              <div className="flex items-center gap-2">
                <SafeIcon icon={FiMail} className="text-gray-400 flex-shrink-0" />
                <a
                  href={`mailto:${email}`}
                  onClick={(e) => handleContactClick(e, 'email', email)}
                  className="text-sm text-indigo-600 hover:text-indigo-800 transition-colors truncate"
                >
                  {email}
                </a>
              </div>
            )}
          </div>

          {/* Services */}
          {services && (
            <div className="mb-4">
              <h5 className="text-sm font-semibold text-gray-800 mb-2">Services</h5>
              <p className="text-sm text-gray-600 leading-relaxed">{services}</p>
            </div>
          )}

          {/* Hours */}
          {hours && (
            <div className="mb-4">
              <div className="flex items-center gap-2 mb-1">
                <SafeIcon icon={FiClock} className="text-gray-400" />
                <h5 className="text-sm font-semibold text-gray-800">Hours</h5>
              </div>
              <p className="text-sm text-gray-600">{hours}</p>
            </div>
          )}

          {/* Notes */}
          {notes && (
            <div className="border-t pt-3">
              <div className="flex items-start gap-2">
                <SafeIcon icon={FiInfo} className="text-gray-400 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-gray-500 leading-relaxed">{notes}</p>
              </div>
            </div>
          )}

          {/* Rating Section */}
          {localRating.total > 0 && (
            <div className="border-t mt-4 pt-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">Is this resource helpful?</span>
                <span className="text-xs text-gray-500">
                  {localRating.total} vote{localRating.total !== 1 ? 's' : ''}
                </span>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => handleVote('up')}
                  disabled={isSubmittingVote}
                  className={`flex-1 flex items-center justify-center gap-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    userVote === 'up'
                      ? 'bg-green-100 text-green-700'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  } ${isSubmittingVote ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <SafeIcon icon={FiThumbsUp} className="text-sm" />
                  <span>{localRating.up}</span>
                </button>
                <button
                  onClick={() => handleVote('down')}
                  disabled={isSubmittingVote}
                  className={`flex-1 flex items-center justify-center gap-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    userVote === 'down'
                      ? 'bg-red-100 text-red-700'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  } ${isSubmittingVote ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <SafeIcon icon={FiThumbsDown} className="text-sm" />
                  <span>{localRating.down}</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </motion.div>

      {/* Vote Confirmation Popup */}
      {showVotePopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl shadow-xl max-w-md w-full p-6"
          >
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Verify Resource</h3>
            <p className="text-gray-600 mb-6">
              Before we connect you, please help others by letting us know if this resource is working.
            </p>
            
            <div className="flex gap-3">
              <button
                onClick={() => handleVote('up')}
                disabled={isSubmittingVote}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-green-100 text-green-700 rounded-lg font-medium hover:bg-green-200 transition-colors disabled:opacity-50"
              >
                <SafeIcon icon={FiThumbsUp} />
                <span>Works</span>
              </button>
              <button
                onClick={() => handleVote('down')}
                disabled={isSubmittingVote}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-red-100 text-red-700 rounded-lg font-medium hover:bg-red-200 transition-colors disabled:opacity-50"
              >
                <SafeIcon icon={FiThumbsDown} />
                <span>Not Working</span>
              </button>
            </div>
            
            <button
              onClick={() => {
                setShowVotePopup(false);
                executePendingAction();
              }}
              className="w-full mt-3 px-4 py-2 text-gray-600 hover:text-gray-800 text-sm"
            >
              Skip and continue
            </button>
          </motion.div>
        </div>
      )}
    </>
  );
};

export default ResourceCard;